import * as React from 'react';
import { FlowScheduleSelectorAction } from '~src/flow/components/FlowScheduleSelector';
import { assertUnreachable } from '~src/ts/helpers/TypescriptHelpers';
import { FlowLinkedTaskOptionsAction } from '../components/FlowLinkedTaskOptions';
import { LinkedTaskFlowSelectorAction } from '../components/LinkedTaskItemSelectorContent';
import { LinkedTaskFlowInitialState, updateInArray } from '../utils/LinkedTaskHelpers';
import { LinkedTaskDialogState, LinkedTaskFlowState, LinkedTaskDialogAddActions, LinkedTaskPositionActions, LinkedTaskItemStateType } from './LinkedTaskStateTypes';

export type LinkedTaskDialogAction =
  FlowScheduleSelectorAction |
  LinkedTaskDialogItemStateAction |
  { type: 'setState', value: LinkedTaskDialogState };

type LinkedTaskDialogItemStateAction =
  FlowLinkedTaskOptionsAction |
  LinkedTaskFlowSelectorAction |
  LinkedTaskDialogAddActions |
  LinkedTaskPositionActions;

function itemsReducer(items: LinkedTaskItemStateType[], action: LinkedTaskDialogItemStateAction): LinkedTaskItemStateType[] {
  let flow;
  let updatedItem;
  switch(action.type) {
    // Adding flows
    case 'addFlow':
      items.splice(action.payload.position !== undefined ? action.payload.position : items.length, 0, action.payload.value);
      return [...items];
    case 'addFlows':
      items.splice(action.payload.position !== undefined ? action.payload.position : items.length, action.payload.overwrite ? 1 : 0, ...action.payload.value);
      return [...items];
    // Adding a new blank item
    case 'addNextTask':
      return [...items, {}];
    case 'moveItem':
      const item = items.splice(action.payload.index, 1)[0];
      switch(action.payload.position) {
        case 'up':
          const index = action.payload.index > 0 ? action.payload.index - 1 : 0;
          items.splice(index, 0, item);
          break;
        case 'down':
          items.splice(action.payload.index + 1, 0, item);
          break;
        case 'beginning':
          items.unshift(item);
          break;
        case 'end':
          items.push(item);
          break;
      }
      return [...items];
    // Flow Options
    case 'setDqwEnabled':
      updatedItem =  {...items[action.payload.index],
        dqwEnabled: action.payload.value
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setStopRemainingTasks':
      updatedItem = {...items[action.payload.index],
        stopRemainingTasks: action.payload.value
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'deleteTaskEntry':
      items.splice(action.value, 1);
      return [...items];
    // DQW Warning
    case 'setEnableTrigger':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        dqw: {...flow.dqw,
          enableTrigger: action.payload.value
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setIsSevere':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        dqw: {...flow.dqw,
          isSevere: action.payload.value
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setMessage':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        dqw: {...flow.dqw,
          message: action.payload.value
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setFlow':
      const existingItem = items[action.payload.index];
      updatedItem = {
        ...LinkedTaskFlowInitialState(),
        ...existingItem,
        flowId: action.payload.flowId,
        flowName: action.payload.flowName
      };
      return updateInArray(action.payload.index, updatedItem, items);
    default:
      assertUnreachable(action);
      return items;
  }
}

function reducer(state: LinkedTaskDialogState, action: LinkedTaskDialogAction): LinkedTaskDialogState {
  switch (action.type) {
    // Setting full state (editing existing linked task)
    case 'setState':
      return action.value;
    // Schedule Selector
    case 'setSelectedSchedule':
      return {
        ...state,
        selectedSchedule: action.value
      };
    default:
      return {
        ...state,
        items: itemsReducer(state.items, action)
      };
  }
}

export interface LinkedTaskDialogStateHookSetupProps {
  selectedSchedule: string;
}

const initialState: LinkedTaskDialogState = {
  selectedSchedule: '',
  items: []
};

export function useLinkedTaskDialogStateHook(props?: LinkedTaskDialogStateHookSetupProps): [LinkedTaskDialogState, React.Dispatch<LinkedTaskDialogAction>] {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  React.useEffect(() => {
    // load state when editing existing linked task
  }, []);
  return [state, dispatch];
}
